<template>
  <div>
    <Head title="Guías de Despacho" />

    <!-- Header & Actions -->
    <div class="flex flex-col sm:flex-row justify-between items-center mb-6 gap-4">
      <h1 class="text-3xl font-bold text-gray-800">Guías de Despacho</h1>
      
      <div class="flex flex-wrap items-center gap-3">
        <!-- Create Button -->
        <Link 
          href="/shipments/create" 
          class="px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg flex items-center gap-2 transition-colors font-medium shadow-sm"
        >
          <i class="fa fa-plus" />
          Nuevo Despacho
        </Link>
      </div>
    </div>

    <!-- Search Tool Bar -->
    <div class="flex gap-4 mb-6">
      <div class="relative flex-1">
        <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <icon name="search" class="w-4 h-4 text-gray-400" />
        </div>
        <input 
          v-model="form.search.terms" 
          type="text" 
          class="block w-full pl-10 pr-3 py-2.5 bg-white border border-gray-200 rounded-lg focus:ring-orange-500 focus:border-orange-500 placeholder-gray-400 transition-colors shadow-sm" 
          placeholder="Buscar por correlativo o transportista..."
        />
      </div>
    </div>

    <!-- Content Table -->
    <div class="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
      <!-- Mobile View -->
      <div class="sm:hidden divide-y divide-gray-100">
        <div v-for="shipment in shipments.data" :key="shipment.id" class="p-4 hover:bg-gray-50 transition-colors">
          <Link :href="`shipments/${shipment.id}`" class="block space-y-3">
            <div class="flex items-start gap-4">
              <div class="w-12 h-12 rounded-lg bg-orange-50 flex items-center justify-center overflow-hidden flex-shrink-0 border border-orange-100">
                <i class="fa fa-file-invoice text-orange-400 text-xl" />
              </div>
              <div class="flex-1">
                <div class="flex justify-between items-start">
                  <h3 class="font-bold text-gray-900 line-clamp-1">{{ shipment.correlative }}</h3>
                  <span 
                    class="ml-2 px-2 py-1 rounded-full text-xs font-bold"
                    :class="getStatusClass(shipment.status)"
                  >
                    {{ getStatusLabel(shipment.status) }}
                  </span>
                </div>
                <p class="text-sm text-gray-500 font-medium mt-1">{{ shipment.carrier?.name || 'Transportista N/A' }}</p>
              </div>
            </div>

            <div class="grid grid-cols-2 gap-2 text-sm text-gray-600 border-t border-gray-100 pt-2">
              <div>
                <span class="block text-xs text-gray-400">Patente / Vehículo</span>
                <span class="font-medium text-gray-800">{{ shipment.transport_unit?.license_plate || 'N/A' }}</span>
              </div>
              <div>
                <span class="block text-xs text-gray-400">Fecha Despacho</span>
                <span class="font-medium text-gray-800">{{ formatDate(shipment.dispatch_date) || 'Sin asignar' }}</span>
              </div>
            </div>
          </Link>
        </div>
      </div>

      <!-- Desktop View -->
      <div class="hidden sm:block overflow-x-auto">
        <table class="w-full whitespace-nowrap">
          <thead>
            <tr class="bg-gray-50/50 text-left border-b border-gray-100">
              <th class="py-4 px-6 font-bold text-xs text-gray-400 uppercase tracking-wider">Correlativo</th>
              <th class="py-4 px-6 font-bold text-xs text-gray-400 uppercase tracking-wider">Transportista</th>
              <th class="py-4 px-6 font-bold text-xs text-gray-400 uppercase tracking-wider">Unidad</th>
              <th class="py-4 px-6 font-bold text-xs text-gray-400 uppercase tracking-wider">Fecha Salida</th>
              <th class="py-4 px-6 font-bold text-xs text-gray-400 uppercase tracking-wider">Órdenes</th>
              <th class="py-4 px-6 font-bold text-xs text-gray-400 uppercase tracking-wider text-center">Estado</th>
              <th class="py-4 px-6 font-bold text-xs text-gray-400 uppercase tracking-wider text-right">Acciones</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-100">
            <tr 
              v-for="shipment in shipments.data" 
              :key="shipment.id"
              class="group hover:bg-gray-50 transition-colors"
            >
              <td class="px-6 py-4">
                <Link :href="`shipments/${shipment.id}`" class="flex items-center gap-3">
                  <div class="w-8 h-8 rounded-lg bg-orange-50 flex items-center justify-center border border-orange-100">
                    <i class="fa fa-file-invoice text-orange-400 text-sm" />
                  </div>
                  <span class="text-gray-900 font-bold hover:text-orange-600">{{ shipment.correlative }}</span>
                </Link>
              </td>
              
              <td class="px-6 py-4 text-gray-600 text-sm">
                {{ shipment.carrier?.name || 'N/A' }}
              </td>

              <td class="px-6 py-4 text-gray-600 text-sm">
                {{ shipment.transport_unit?.license_plate || 'N/A' }}
              </td>

              <td class="px-6 py-4 text-gray-600 text-sm">
                {{ formatDate(shipment.dispatch_date) || 'No asignada' }}
              </td>

              <td class="px-6 py-4 text-gray-600 text-sm font-medium">
                {{ shipment.orders_count || 0 }} órdenes
              </td>

              <td class="px-6 py-4 text-center">
                <span 
                  class="inline-block px-2 py-1 rounded-full text-xs font-bold"
                  :class="getStatusClass(shipment.status)"
                >
                  {{ getStatusLabel(shipment.status) }}
                </span>
              </td>

              <td class="px-6 py-4 text-right">
                <div class="flex items-center justify-end gap-2">
                  <a 
                    :href="`/shipments/${shipment.id}/pdf`" 
                    target="_blank"
                    class="text-emerald-500 hover:text-emerald-600 transition-colors px-2"
                    title="Imprimir Guía PDF"
                  >
                    <i class="fa fa-file-pdf" />
                  </a>
                  <Link 
                    :href="`shipments/${shipment.id}`" 
                    class="text-gray-400 hover:text-blue-500 transition-colors px-2"
                    title="Ver detalle"
                  >
                    <i class="fa fa-eye" />
                  </Link>
                </div>
              </td>
            </tr>

            <tr v-if="!shipments?.data?.length">
              <td colspan="7" class="py-20 text-center">
                <div class="flex flex-col items-center justify-center text-gray-400">
                  <div class="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                    <i class="fa fa-boxes text-2xl opacity-50" />
                  </div>
                  <h3 class="text-lg font-medium text-gray-900 mb-1">No hay guías de despacho</h3>
                  <p class="text-gray-500 text-sm max-w-sm">
                    Comienza agrupando órdenes para generar un nuevo despacho.
                  </p>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Pagination -->
    <div class="mt-6 flex justify-center">
      <pagination 
        v-if="shipments?.meta?.links"
        :links="shipments.meta.links"
        :query-params="currentQueryParams"
      />
    </div>

    <!-- Quick Links (Footer) -->
    <div class="mt-8 pt-6 border-t border-gray-100">
      <h3 class="text-sm font-medium text-gray-700 mb-3 ml-1">Logística</h3>
      <div class="flex flex-wrap gap-3">
        <Link 
          href="/carriers"
          class="px-3 py-2 bg-white border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50 flex items-center gap-2 transition-colors text-sm font-medium hover:border-orange-200"
        >
          <i class="fa fa-truck text-orange-400" />
          Transportistas
        </Link>
        <Link
          href="/transport-units" 
          class="px-3 py-2 bg-white border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50 flex items-center gap-2 transition-colors text-sm font-medium hover:border-orange-200"
        >
          <i class="fa fa-car text-orange-400" />
          Unidades
        </Link>
      </div>
    </div>
  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/inertia-vue3'
import Icon from '@/Shared/Icon'
import Layout from '@/Shared/Layout'
import Pagination from '@/Shared/Pagination.vue'
import throttle from 'lodash/throttle'
import pickBy from 'lodash/pickBy'

export default {
  name: 'ShipmentsIndex',
  
  components: {
    Pagination,
    Head,
    Icon,
    Link,
  },

  layout: Layout,

  props: {
    shipments: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      form: this.$inertia.form({
        search: {
          terms: '',
        },
      }),
    }
  },

  computed: {
    currentQueryParams() {
      return { search: pickBy(this.form.search) }
    },
  },

  watch: {
    'form.search': {
      deep: true,
      handler: throttle(function() {
        this.$inertia.get(
          '/shipments',
          { search: pickBy(this.form.search), page: 1 },
          { preserveState: true },
        )
      }, 300),
    },
  },

  methods: {
    getStatusLabel(status) {
      const labels = {
        'pending': 'Por Iniciar',
        'in_progress': 'En Curso',
        'delivered': 'Entregado',
      }
      return labels[status] || status
    },
    
    getStatusClass(status) {
      if (status === 'pending') return 'bg-yellow-100 text-yellow-800'
      if (status === 'in_progress') return 'bg-blue-100 text-blue-800'
      if (status === 'delivered') return 'bg-green-100 text-green-800'
      return 'bg-gray-100 text-gray-800'
    },

    formatDate(dateStr) {
      if (!dateStr) return null
      const date = new Date(dateStr)
      return new Intl.DateTimeFormat('es-VE', { 
        day: '2-digit', 
        month: '2-digit', 
        year: 'numeric', 
      }).format(date)
    },
  },
}
</script>
